import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const Ctx = createContext({ settings: null });
export const SETTINGS_UPDATED_KEY = "mdr-settings-updated";

export const has = (v) => typeof v === "string" && v.trim().length > 0;

export const channelLink = (icon, contact) => {
  if (!contact) return null;
  const key = icon === "mail" ? "email" : icon;
  const raw = (contact[key] || "").trim();
  if (!raw) return null;
  const number = raw.replace(/[^\d+]/g, "").replace(/^00/, "+");
  if (icon === "phone") return { href: `tel:${number}`, text: raw, external: false };
  if (icon === "mail") return { href: `mailto:${raw}`, text: raw, external: false };
  if (icon === "whatsapp") return { href: `https://wa.me/${number.replace(/\D/g, "")}`, text: raw, external: true };
  if (icon === "instagram")
    return { href: raw.startsWith("http") ? raw : `https://instagram.com/${raw.replace("@", "")}`, text: raw, external: true };
  if (icon === "facebook")
    return { href: raw.startsWith("http") ? raw : `https://facebook.com/${raw}`, text: raw, external: true };
  return null;
};

export const branchChannelLink = (branch, channel, contact) =>
  channelLink(channel, { [channel]: contact?.[`${branch}_${channel}`] });

export const whatsappBookingLink = (branch, contact, template) => {
  const link = branchChannelLink(branch.id, "whatsapp", contact);
  if (!link) return null;
  const text = template.replace("{city}", branch.city);
  return `${link.href}?text=${encodeURIComponent(text)}`;
};

export const getFaqAnswers = (item, settings, t, lang) => {
  if (item.source === "systems") {
    const specs = t.systems.specs.map((spec) => {
      const value = settings?.systems?.[spec.icon === "wifi" ? "internet" : spec.icon];
      return has(value) ? `${spec.label}: ${value}` : null;
    }).filter(Boolean);
    return specs.length ? [specs.join(" • ")] : [];
  }
  if (item.source === "hours") {
    return t.branches.items.flatMap((branch) => {
      const value = settings?.hours?.[branch.id];
      return has(value) ? [`${branch.city}: ${value}`] : [];
    });
  }
  if (item.source === "prices") {
    const fmt = new Intl.NumberFormat(lang === "tr" ? "tr-TR" : "en-GB", { maximumFractionDigits: 2 });
    return t.branches.items.flatMap((branch) => {
      const value = settings?.prices?.[branch.id];
      const extras = (settings?.price_items?.[branch.id] || [])
        .filter((p) => has(p.label) && p.price != null)
        .map((p) => `${p.label}: ${fmt.format(p.price)} TL${p.unit === "flat" ? ` (${t.pricing.flat})` : ` / ${t.pricing.hour}`}`);
      const parts = value == null ? [] : [`${t.pricing.pc}: ${fmt.format(value)} TL / ${t.pricing.hour}`];
      const all = [...parts, ...extras];
      return all.length ? [`${branch.city} — ${all.join(" • ")}`] : [];
    });
  }
  return item.a;
};


export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let pending;
    const refresh = async () => {
      pending?.abort();
      const controller = new AbortController();
      pending = controller;
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setSettings(data);
      } catch {
        // Keep the last loaded settings if a refresh fails or is cancelled.
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onStorage = (event) => {
      if (event.key === SETTINGS_UPDATED_KEY) refreshWhenVisible();
    };
    refresh();
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("pageshow", refreshWhenVisible);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      pending?.abort();
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("pageshow", refreshWhenVisible);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  return <Ctx.Provider value={{ settings }}>{children}</Ctx.Provider>;
};

export const useSettings = () => useContext(Ctx);
